import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";

const menuItems = [
  { to: "/", text: "ראשי" },
  { to: "/about", text: "אודות" },
  { to: "/services", text: "שירותים" },
  { to: "/portfolio", text: "תיק עבודות" },
  { to: "/growth-os", text: "מערכת צמיחה אוטונומית" },
  { to: "/contact", text: "צור קשר" },
];

const Navigation: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [progress, setProgress] = useState(0);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      setScrolled(y > 24);
      const max = document.documentElement.scrollHeight - window.innerHeight;
      setProgress(max > 0 ? Math.min(1, y / max) : 0);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = isMenuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isMenuOpen]);

  useEffect(() => {
    if (!isMenuOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setIsMenuOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isMenuOpen]);

  const isActive = (to: string) => location.pathname === to;

  return (
    <>
      <nav
        className={`fixed inset-x-0 top-0 z-50 transition-colors duration-500 ease-out-quart ${
          scrolled || isMenuOpen
            ? "border-b border-[var(--line)] bg-ink/92 backdrop-blur-xl"
            : "border-b border-transparent bg-transparent"
        }`}
      >
        <div className="shell flex items-center justify-between py-2.5">
          <Link
            to="/"
            className="group flex items-center"
            id="site-logo-link"
            aria-label="ELAD DIGITAL, לעמוד הבית"
          >
            <img
              id="site-logo"
              src="/images/logo-mark.png"
              alt="ELAD DIGITAL"
              width={611}
              height={164}
              className="h-9 w-auto object-contain transition-opacity duration-300 group-hover:opacity-80 sm:h-11"
            />
          </Link>

          {/* Desktop */}
          <div className="hidden items-center gap-1 lg:flex">
            {menuItems.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                aria-current={isActive(item.to) ? "page" : undefined}
                className={`relative px-3.5 py-2 text-[0.9rem] font-normal transition-colors duration-200 ${
                  isActive(item.to)
                    ? "text-signal"
                    : "text-fg-2 hover:text-fg"
                }`}
              >
                {item.text}
                <span
                  className={`absolute inset-x-3.5 -bottom-0.5 h-px origin-right bg-signal transition-transform duration-300 ease-out-expo ${
                    isActive(item.to) ? "scale-x-100" : "scale-x-0"
                  }`}
                />
              </Link>
            ))}
            <a
              href="tel:+972557070007"
              className="btn btn-primary ms-4 px-5 py-2.5 text-[0.9rem]"
            >
              <Phone className="h-3.5 w-3.5" />
              <span>התקשרו עכשיו</span>
            </a>
          </div>

          <button
            onClick={() => setIsMenuOpen((v) => !v)}
            className="relative z-[60] -me-2 p-2 text-fg transition-colors hover:text-signal lg:hidden"
            aria-label={isMenuOpen ? "סגירת תפריט" : "פתיחת תפריט"}
            aria-expanded={isMenuOpen}
            aria-controls="mobile-menu"
          >
            {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* Reading progress */}
        <div
          aria-hidden="true"
          className="absolute inset-x-0 bottom-0 h-px origin-right bg-signal/70"
          style={{
            transform: `scaleX(${progress})`,
            opacity: scrolled ? 1 : 0,
            transition: "opacity 300ms ease",
          }}
        />
      </nav>

      {/* Mobile menu */}
      {isMenuOpen && (
        <div
          id="mobile-menu"
          className="fixed inset-0 z-40 bg-ink lg:hidden"
          style={{ animation: "menuIn 320ms cubic-bezier(0.16,1,0.3,1) both" }}
        >
          <div className="shell flex h-full flex-col justify-center pb-16 pt-24">
            <nav className="flex flex-col">
              {menuItems.map((item, index) => (
                <Link
                  key={item.to}
                  to={item.to}
                  onClick={() => setIsMenuOpen(false)}
                  aria-current={isActive(item.to) ? "page" : undefined}
                  className={`flex items-baseline gap-4 border-b border-[var(--line)] py-4 font-display text-[1.9rem] font-extrabold tracking-[-0.03em] transition-colors ${
                    isActive(item.to) ? "text-signal" : "text-fg hover:text-signal"
                  }`}
                  style={{
                    animation: `menuItemIn 460ms cubic-bezier(0.16,1,0.3,1) ${index * 55 + 90}ms both`,
                  }}
                >
                  <span className="t-data text-xs text-fg-3">
                    {String(index + 1).padStart(2, "0")}
                  </span>
                  <span>{item.text}</span>
                </Link>
              ))}
            </nav>

            <div
              className="mt-10 flex flex-col gap-3"
              style={{
                animation: `menuItemIn 460ms cubic-bezier(0.16,1,0.3,1) ${menuItems.length * 55 + 120}ms both`,
              }}
            >
              <a
                href="tel:+972557070007"
                className="btn btn-primary w-full py-4 text-base"
                onClick={() => setIsMenuOpen(false)}
              >
                <Phone className="h-4 w-4" />
                <span>התקשרו עכשיו</span>
              </a>
              <a
                href="https://wa.me/972557070007"
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-ghost w-full py-4 text-base"
                onClick={() => setIsMenuOpen(false)}
              >
                שלחו הודעה בוואטסאפ
              </a>
              <p className="t-data mt-2 text-center text-sm text-fg-3" dir="ltr">
                055-7070007
              </p>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes menuIn {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        @keyframes menuItemIn {
          from { opacity: 0; transform: translateY(14px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @media (prefers-reduced-motion: reduce) {
          #mobile-menu, #mobile-menu * { animation: none !important; }
        }
      `}</style>
    </>
  );
};

export default Navigation;
